/**
 * useScene — derive the active Lobby scene from a LandingResponse.
 *
 * Phase 01 ships 'ask' and 'foresight'. The server already runs the decision
 * tree and stamps `response.scene`. The hook just reads it with a safe
 * default for older clients / fallback shapes.
 *
 * It also exposes a `dismissForesight()` setter so the visitor can collapse
 * a wrong-guess foresight back to the Ask scene — the "Not what you meant?"
 * exit ramp from the design corrections.
 *
 * Spec: docs/strategy/visitor-pwa-redesign-lobby-2026-05-15.html
 *       docs/strategy/visitor-pwa-design-research-2026-05-15.md (correction 3)
 */
import { useState, useMemo } from 'react';
import type { LandingResponse, Scene } from '@tendedloop/shared';

export interface SceneState {
  /** The resolved scene the page should render. */
  scene: Scene;
  /** True iff scene === 'foresight' AND anticipated is present. UI guard. */
  showForesight: boolean;
  /**
   * User-driven dismissal for the current foresight card. Once dismissed,
   * `scene` collapses to 'ask' for the rest of the page lifecycle.
   */
  dismissForesight: () => void;
}

export function useScene(landing: LandingResponse | null): SceneState {
  const [foresightDismissed, setForesightDismissed] = useState(false);

  return useMemo(() => {
    // No landing yet (or fallback shape with scene='ask') → ask
    const raw: Scene = landing?.scene ?? 'ask';
    const hasForesight = !!landing?.anticipated;
    const wantForesight = raw === 'foresight' && hasForesight && !foresightDismissed;
    const scene: Scene = wantForesight ? 'foresight' : raw === 'foresight' ? 'ask' : raw;
    return {
      scene,
      showForesight: wantForesight,
      dismissForesight: () => setForesightDismissed(true),
    };
  }, [landing, foresightDismissed]);
}
